<template>
  <Loader :show="!productReady" v-if="!productReady"></Loader>
  <div v-else>
    <div v-if="productReady && product" class="d-flex flex-column h-100">
      <MDBRow>
        <MDBCol cols="12">
          <Breadcrumbs :parent-routes="['shop']" :route-name="productApi.name[currentLocale]" :showGoBack="true"
            class="pb-3" />
        </MDBCol>
      </MDBRow>
      <MDBRow class="flex-grow-1">
        <MDBCol xl="7" cols="12">
          <MDBCard class="mb-3 mb-md-0 product-image-wrap product-image-wrap--height">
            <MDBCardBody class="d-flex justify-content-center p-relative">
              <div class="video" v-if="product.video">
                <iframe class="responsive-iframe" :src="product.video" allow="fullscreen" title="" data-ready="true"
                  tabindex="-1"></iframe>
              </div>
              <img v-else :src="productImgSrc" :alt="$t('productImg')" class="product-image" />
              <Loader v-if="canSeeFavorite && isSavingFavorite" :show="isSavingFavorite" :small="true" :tiny="true"
                class="p-absolute heart-icon-container text-danger" />
              <MDBIcon v-else-if="canSeeFavorite" icon="heart" size="2x" :iconStyle="isFavoriteProduct ? 'fas' : 'far'"
                class="p-absolute heart-icon-container cursor--pointer text-danger px-2 py-2" @click="saveFavorite()" />
            </MDBCardBody>
          </MDBCard>
        </MDBCol>

        <MDBCol xl="5" cols="12">
          <MDBCard class="product-main-data product-image-wrap">
            <MDBCardBody class="d-flex flex-column justify-content-between">
              <div class="d-flex flex-column">
                <p v-if="productApi?.vendor && productApi?.vendor[currentLocale]">{{ productApi.vendor[currentLocale] }}
                </p>
                <MDBCardTitle class="font-25"><span>{{ productApi.name[currentLocale] }}</span>
                </MDBCardTitle>
                <MDBCardTitle class="font-45 my-3" :class="maxQuantity == 0 ? 'text-warning' : 'text-success'">
                  {{ points }}
                  <span class="font-25 text-dark">{{ $t("bankstatement.points") }}</span>
                </MDBCardTitle>
                <template v-if="variants.length > 0">
                  <select class="d-flex" @change="(event) => selectVariant(event)" :disabled="isAddingToCart">
                    <option v-for="(variant, id) in variants" :key="`variant_${variant.id}`" :value="id"
                      :selected="selectedVariant == id">
                      {{ variant.label }}
                    </option>
                  </select>
                </template>
              </div>
              <template v-if="product.status === PRODUCT_STATUS.VISIBLE_NOT_ALLOWED_TO_BUY">
                {{ notAvailabilityMessage }}
              </template>
              <template v-else-if="product.status !== PRODUCT_STATUS.ACTIVE">
                {{ $t("shop.timeUnavailable") }}
              </template>
              <template v-else>
                <div class="pb-5">
                  <NumberInput :label="$t('shop.quantity')" :step="1" :min="1" :max="maxQuantity" v-model="quantity"
                    :disabled="isAddingToCart">
                  </NumberInput>
                </div>
                <MDBBtn :color="maxQuantity == 0 ? 'warning' : 'success'" class="w-100"
                  @click.prevent="addProduct(product)" :disabled="isAddingToCart">
                  {{ $t("global.buttons.toTheBasket") }}
                </MDBBtn>
              </template>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
    </div>
  </div>

</template>

<script setup>
import Loader from "@/components/General/Loader/index";
import {
  MDBRow,
  MDBCol,
  MDBCard,
  MDBCardBody,
  MDBCardTitle,
  MDBBtn, MDBIcon
} from "mdb-vue-ui-kit";
import Breadcrumbs from "@/components/General/Breadcrumbs/index";
import NumberInput from "@/components/Shared/NumberInput.vue";
import { useStore } from "vuex";
import { computed, watch, ref } from "vue";
import { useRoute } from "vue-router";
import { ProductService } from "@/services/product.service";
import TokenService from "@/services/storage.service";

import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";

import { useI18n } from "vue-i18n";
import { PRODUCT_STATUS } from "@/configs/constants";
const { t } = useI18n() // Import the translation function from i18n

const store = useStore();
const route = useRoute();

const currentLocale = computed(() => store.getters.getCurrentLocale);
const quantity = ref(1);
const isAddingToCart = ref(false);
const isWholesaler = ref(false);
const isWholesalerStaff = ref(false);
const isStaff = ref(false);
const product = ref(null);
const variants = ref(null);
const productImgSrc = ref(null);
const isFavoriteProduct = ref(false)
const selectedVariant = ref('')
const canSeeFavorite = ref(false);
const isSavingFavorite = ref(false);

const noPointsToast = () =>
  toast(t("shop.notEnoughPoints"), {
    autoClose: 10000,
    type: "warning",
    theme: "colored",
    transition: toast.TRANSITIONS.SLIDE,
    position: toast.POSITION.BOTTOM_RIGHT,
  });

const productApi = computed(() => store.getters.getProduct);
const productReady = computed(() => {
  if (!productApi.value || !product.value) {
    return false;
  }
  return String(productApi.value.id) === String(route.params.id);
});
const notAvailabilityMessage = computed(() => {
  const translations = productApi.value?.not_availability_information;
  const localized =
    translations && typeof translations === "object"
      ? translations[currentLocale.value]
      : typeof translations === "string"
        ? translations
        : "";

  if (typeof localized === "string" && localized.trim()) {
    return localized.trim();
  }

  return t("shop.outOfStock");
});

const getProductPointsValue = (productData) => {
  const rawPoints = productData?.points;

  if (rawPoints == null) {
    return 0;
  }

  if (typeof rawPoints === "object") {
    const localizedValue = rawPoints;
    const normalizedLocalizedValue = Number(localizedValue ?? 0);
    return Number.isFinite(normalizedLocalizedValue)
      ? normalizedLocalizedValue
      : 0;
  }

  const normalizedRawValue = Number(rawPoints);
  return Number.isFinite(normalizedRawValue) ? normalizedRawValue : 0;
};

watch(productApi, (newVal) => {
  if (newVal) {
    let hasVariants = newVal.variants && newVal.variants.length > 0;
    product.value = hasVariants
      ? newVal.variants.find((item) => item.is_default == true)
      : newVal;
    variants.value = hasVariants
      ? [...newVal.variants]
        .sort((a, b) => b.is_default - a.is_default)
        .map((item) => {
          const localizedTitle =
            item?.title &&
              typeof item.title === "object" &&
              !Array.isArray(item.title)
              ? item.title[currentLocale.value]
              : null;

          if (localizedTitle) {
            item["label"] = localizedTitle;
            return item;
          }

          let attributeOption = [];
          try {
            attributeOption = JSON.parse(item.attribute_option || "[]");
          } catch (error) {
            attributeOption = [];
          }

          item["label"] = attributeOption
            .map((attributeItem) => `${attributeItem.label}: ${attributeItem.option}`)
            .join(", ");
          return item;
        })
      : [];
    const isVariant = newVal.has_variants === true

    productImgSrc.value = product.value?.imageUrl;

    if (isVariant) {
      isFavoriteProduct.value = newVal && user.value && user.value.favorites.find((item) => item.product_id == newVal.plainId && item.product_variant_id == product.value.id)
    } else {
      isFavoriteProduct.value = newVal && user.value && user.value.favorites.find((item) => item.product_id == newVal.plainId)
    }
  }
});

const user = computed(() => store.getters.getMe);
const cart = computed(() => store.getters.getCart);
const maxQuantity = computed(() => {
  const productPoints = getProductPointsValue(product.value);
  if (!productPoints) {
    return 0;
  }

  return Math.floor(
    ((user.value?.points?.avail_points || 0) - (cart.value?.total || 0)) /
    productPoints
  );
});
const points = computed(() => getProductPointsValue(product.value) * quantity.value);

watch(user, (newVal) => {
  isWholesaler.value = !!newVal?.roles?.find((item) => item.name == "wholesaler");
  isWholesalerStaff.value = !!newVal?.roles?.find((item) => item.name == "wholesalerStaff");
  isStaff.value = !!newVal?.roles?.find((item) => item.name == "staff");
  canSeeFavorite.value = TokenService.showElement("shopFavorites.see");

  if (!newVal || !productApi.value) {
    isFavoriteProduct.value = false;
    return;
  }

  if (productApi.value.has_variants) {
    isFavoriteProduct.value = newVal.favorites?.find((item) => item.product_id == productApi.value.plainId && item.product_variant_id == product.value?.id);
  } else {
    isFavoriteProduct.value = newVal.favorites?.find((item) => item.product_id == productApi.value.plainId);
  }
});

const selectVariant = (event) => {
  product.value = variants.value[event.target.value];
  productImgSrc.value = variants.value[event.target.value].imageUrl;
  isFavoriteProduct.value = user.value && user.value.favorites.find((item) => item.product_id == productApi.value.plainId && item.product_variant_id == product.value.id)
  selectedVariant.value = product.value.id
};

const addProduct = async () => {
  if (maxQuantity.value == 0 ||
    isWholesalerStaff.value ||
    isStaff.value) {
    noPointsToast();
  } else {
    isAddingToCart.value = true;
    try {
      let data = {
        id: productApi.value.id,
        quantity: quantity.value,
      };

      if (productApi.value.has_variants && productApi.value.variants?.length > 0) {
        data["variant_id"] = product.value.id;
      }

      const toastMessage =
        quantity.value > 1
          ? t("toast.addProducts")
          : t("toast.addProduct");

      await store.dispatch("addItem", data);
      await store.dispatch("fetchUserShopCart");

      toast(toastMessage, {
        type: "success",
        theme: "colored",
        transition: toast.TRANSITIONS.SLIDE,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } finally {
      isAddingToCart.value = false;
    }
  }

};

const saveFavorite = async () => {
  if (isSavingFavorite.value) {
    return;
  }

  isSavingFavorite.value = true;
  const wasFavorite = Boolean(isFavoriteProduct.value);
  try {
    const data = {
      user_id: user.value.plainId,
      product_id: productApi.value.plainId,
      product_variant_id: productApi.value.has_variants ? product.value.id : null
    }
    const response = await ProductService.saveDeleteFavorite(data)
    if (!response || response.status >= 400) {
      const fallbackMessage = t("toast.waitMoment");
      const errorMessage =
        response?.data?.message ||
        response?.data?.error ||
        fallbackMessage;

      toast(errorMessage, {
        autoClose: 10000,
        type: "error",
        theme: "colored",
        transition: toast.TRANSITIONS.SLIDE,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    }

    await store.dispatch("me");
    const toastMessage = wasFavorite
      ? t("toast.favoriteRemoved")
      : t("toast.favoriteAdded");

    toast(toastMessage, {
      autoClose: 8000,
      type: "success",
      theme: "colored",
      transition: toast.TRANSITIONS.SLIDE,
      position: toast.POSITION.BOTTOM_RIGHT,
    });
  } finally {
    isSavingFavorite.value = false;
  }
}

watch(
  () => route.params.id,
  async (newId) => {
    if (!newId) {
      return;
    }
    store.commit("SET_PRODUCT", null);
    await store.dispatch("loadProductById", newId);
    await store.dispatch("clearAttributes");
    await store.dispatch("fetchUserShopCart");

    if (maxQuantity.value == 0 && isWholesalerStaff.value == false) {
      noPointsToast();
    }
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
@import "../../styles/variables";
@import "../../styles/product";
</style>
